import React, { useCallback, useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authUtils } from '../services/auth';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Header from '../components/common/Header';
import Toast from '../components/common/Toast';
import NotFound from './NotFound';
import { PracticeJobHint } from '../components/competition/LiveTerminals';
import CountdownBanner from '../components/competition/CountdownBanner';
import config from '../config/environment';
import './Competition.css';
import { Competition } from '../types/interfaces';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { availableTabs, CompetitionTab, competitionView, resolveTab } from './competition/competitionView';
import CompetitionHeaderStrip from './competition/CompetitionHeaderStrip';
import CompetitionTabs from './competition/CompetitionTabs';
import OverviewTab from './competition/OverviewTab';
import SubmitTab from './competition/SubmitTab';
import LeaderboardTab from './competition/LeaderboardTab';
import { useTourRole } from '../components/tour/tourContext';

const CompetitionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [competition, setCompetition] = useState<Competition | null>(null);
  useDocumentTitle(competition?.title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'not-found' | 'private' | 'unauthorized' | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownError, setMarkdownError] = useState('');

  // Access control for submission/join
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [accessType, setAccessType] = useState<string | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Shared practice-run state lifted from the upload handler so the live
  // terminals card can seed instantly (see handlers below).
  const [practiceJobHint, setPracticeJobHint] = useState<PracticeJobHint | null>(null);
  const [practiceRefetch, setPracticeRefetch] = useState(0);
  const [practiceClearNotice, setPracticeClearNotice] = useState(0);
  // Bumped whenever the history table should look again.
  const [historyRefresh, setHistoryRefresh] = useState(0);
  // Whether the caller's most recent run is still in flight. LiveTerminals
  // already polls the job state, so it reports it here rather than us opening a
  // second request. null = unknown (no practice card on screen), which must
  // leave the download button enabled: graded runs have no live card, and their
  // in-flight case is caught server-side by a 409.
  const [runInFlight, setRunInFlight] = useState<boolean | null>(null);
  // Whether this user has ANY run whose logs could exist. null = unknown
  // (fail open); false = they have never submitted, so the download button
  // has nothing to fetch and previously answered with a raw error.
  const [hasAnyRun, setHasAnyRun] = useState<boolean | null>(null);
  // Whether this competition has practice runs at all (LiveTerminals reads it
  // from the server). Run-log controls only make sense where it does.
  const [practiceSupported, setPracticeSupported] = useState(false);
  // A new competition starts unknown, so the previous one's run controls never flash.
  useEffect(() => { setPracticeSupported(false); }, [id]);

  const handleJobStateChange = useCallback((state: string | null, terminal: boolean) => {
    setRunInFlight(state ? !terminal : null);
    // A run that just finished has a score worth showing.
    if (terminal) setHistoryRefresh((n) => n + 1);
  }, []);

  const handlePracticeStarted = useCallback((hint: PracticeJobHint) => {
    setPracticeJobHint(hint);
    setPracticeRefetch((n) => n + 1);
    setHistoryRefresh((n) => n + 1);
  }, []);
  const handlePracticeConflict = useCallback(() => {
    setPracticeRefetch((n) => n + 1);
  }, []);
  const handleRunCanceled = useCallback(() => {
    setPracticeClearNotice((n) => n + 1);
    setHistoryRefresh((n) => n + 1);
  }, []);

  // Host/admin management CTA
  const [isHostForCompetition, setIsHostForCompetition] = useState(false);
  const [checkingHost, setCheckingHost] = useState(false);
  const [myTeam, setMyTeam] = useState<any | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const isTeamMode = (competition?.competition_mode || 'individual') === 'team';
  const isTeamLead = Boolean(user?.username && myTeam?.lead_username && user.username === myTeam.lead_username);
  const normalizedPrize = (competition?.prize || '').toLowerCase();
  const prizeTagLabel =
    normalizedPrize === 'money'
      ? 'Prize: Money prizes'
      : normalizedPrize === 'badge'
        ? 'Prize: Certificate'
        : null;

  const checkAccess = useCallback(async (cid: number) => {
    if (!cid) return;
    try {
      setCheckingAccess(true);
      setAccessError('');
      const token = authUtils.getStoredToken();
      const response = await apiService.post(`/competitions/check/${cid}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data;
      setHasAccess(!!data.has_access);
      setAccessType(data.access_type ?? null);
    } catch (err) {
      setAccessError('Failed to check competition access');
      console.error('Error checking access:', err);
      setHasAccess(false);
      setAccessType(null);
    } finally {
      setCheckingAccess(false);
    }
  }, []);

  const [downloadingLogs, setDownloadingLogs] = useState(false);

  const handleDownloadRunLogs = async () => {
    if (!competition?.id) return;
    try {
      setDownloadingLogs(true);
      const token = authUtils.getStoredToken();
      const response = await apiService.post(`/evaluations/${competition.id}/logs`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const { url } = response.data;
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error downloading run logs:', err);
      setToastMessage(err?.message || 'Failed to download run logs.');
    } finally {
      setDownloadingLogs(false);
    }
  };

  const handleJoin = async () => {
    if (!competition?.id) return;
    try {
      setCheckingAccess(true);
      setAccessError('');
      const token = authUtils.getStoredToken();
      await apiService.post(`/competitions/join/${competition.id}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // re-check access after joining
      await checkAccess(competition.id);
      // show success toast
      if (competition.status === 'open') {
        setToastMessage('Successfully joined the competition. You can now submit your file.');
      } else {
        setToastMessage('Successfully joined the competition. Submissions will open when the competition status is Open.');
      }
    } catch (err) {
      setAccessError('Failed to join competition');
      console.error('Error joining competition:', err);
      setToastMessage('Failed to join competition.');
    } finally {
      setCheckingAccess(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const fetchCompetition = async () => {
      try {
        setLoading(true);
        setError('');
        setErrorType(null);
        const token = authUtils.getStoredToken();
        const response = await apiService.post(`/competitions/${id}`, {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompetition(response.data);
      } catch (err: any) {
        console.error('Error loading competition:', err);
        const status = err.response?.status;
        const errorMessage = err.response?.data?.message || err.message || '';

        if (status === 404) {
          setErrorType('not-found');
          setError('Competition not found');
        } else if (status === 403 || errorMessage.toLowerCase().includes('private')) {
          setErrorType('private');
          setError('This competition is private');
        } else if (status === 401 || !isAuthenticated) {
          setErrorType('unauthorized');
          setError('You need to be logged in');
        } else {
          setError('Failed to load competition details');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCompetition();
  }, [id, isAuthenticated]);

  // When competition loads, check access
  useEffect(() => {
    if (!competition?.id) return;
    checkAccess(competition.id);
  }, [competition?.id, checkAccess]);

  // When competition loads, check if user is a host for this competition.
  useEffect(() => {
    if (!competition?.id || !user?.username) return;

    // Platform admins can always manage.
    if (user.role === 'admin') {
      setIsHostForCompetition(false);
      return;
    }

    const run = async () => {
      setCheckingHost(true);
      try {
        const res = await apiService.get(`/hosts/competition/${competition.id}`);
        const hosts = Array.isArray(res.data) ? res.data : [];
        setIsHostForCompetition(hosts.some((h: any) => String(h.username) === String(user.username)));
      } catch (err) {
        console.error('Failed to check host list', err);
        setIsHostForCompetition(false);
      } finally {
        setCheckingHost(false);
      }
    };

    run();
  }, [competition?.id, user?.role, user?.username]);

  useEffect(() => {
    if (!competition?.id) return;
    if (!config?.FEATURES?.TEAM_MODE_ENABLED) {
      setMyTeam(null);
      return;
    }
    if ((competition.competition_mode || 'individual') !== 'team') {
      setMyTeam(null);
      return;
    }

    const run = async () => {
      setTeamLoading(true);
      try {
        const res = await apiService.post(`/teams/my/${competition.id}`, {});
        setMyTeam(res.data || null);
      } catch (err) {
        console.error('Failed to load team membership', err);
        setMyTeam(null);
      } finally {
        setTeamLoading(false);
      }
    };
    run();
  }, [competition?.id, competition?.competition_mode]);

  // Fetch markdown instructions
  useEffect(() => {
    if (!competition?.instructions_path) return;
    
    const fetchMarkdown = async () => {
      try {
        setMarkdownLoading(true);
        setMarkdownError('');
        const response = await fetch(competition.instructions_path!);
        if (!response.ok) {
          throw new Error('Failed to fetch instructions');
        }
        const text = await response.text();
        setMarkdownContent(text);
      } catch (err) {
        setMarkdownError('Failed to load instructions');
        console.error('Error loading markdown:', err);
      } finally {
        setMarkdownLoading(false);
      }
    };

    fetchMarkdown();
  }, [competition?.instructions_path]);

  const view = competitionView({ status: competition?.status, hasAccess, accessType,
    isHost: isHostForCompetition, isAdmin: user?.role === 'admin' });
  useTourRole(view.action === 'evaluate' ? 'judge'
    : view.action === 'manage' ? 'host'
      : hasAccess && accessType === 'competitor' ? 'participant' : null);
  const tabs = availableTabs(view.canSubmitTab, competition?.leaderboard);
  const activeTab = resolveTab(searchParams.get('tab'), tabs, view.defaultTab);
  const changeTab = (tab: CompetitionTab) => setSearchParams({ tab }, { replace: true });
  const onAction = () => {
    if (view.action === 'join') handleJoin();
    else if (view.action === 'evaluate') navigate(`/evaluations/${competition!.id}`);
    else if (view.action === 'manage') navigate(`/manage/${competition!.id}`);
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return <LoadingSpinner text="Loading competition details..." />;
  }

  if (errorType) {
    return <NotFound errorType={errorType} message={error} />;
  }

  if (error || !competition) {
    return <NotFound errorType="not-found" message={error || 'Competition not found.'} />;
  }

  return (
    <>
      <Header />
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
      <div className={`competition-page${view.action ? ' has-pinned-action' : ''}`}>
        <CompetitionHeaderStrip competition={competition} prizeLabel={prizeTagLabel ? prizeTagLabel.replace(/^Prize:\s*/, '') : null}
          action={view.action} actionBusy={checkingAccess || checkingHost} onAction={onAction} />
        {accessError && <p className="competition-card markdown-error" role="alert">{accessError}</p>}
        <CountdownBanner competitionId={Number(competition.id)} />
        <CompetitionTabs tabs={tabs} active={activeTab} onChange={changeTab} />
        <div role="tabpanel" tabIndex={0} id={`competition-panel-${activeTab}`} aria-labelledby={`competition-tab-${activeTab}`}
          data-tour={activeTab === 'leaderboard' ? 'competition-leaderboard' : undefined}>
          {activeTab === 'overview' && <OverviewTab competition={competition}
            instructions={{ loading: markdownLoading, error: markdownError, content: markdownContent }} />}
          {activeTab === 'submit' && <SubmitTab competition={competition} isTeamMode={isTeamMode} teamLoading={teamLoading}
            myTeam={myTeam} isTeamLead={isTeamLead} canUpload={view.canUpload} previewOnly={view.previewOnly}
            practice={{ jobHint: practiceJobHint, refetchSignal: practiceRefetch, clearNoticeSignal: practiceClearNotice,
              historyRefresh, onStarted: handlePracticeStarted, onConflict: handlePracticeConflict,
              onRunCanceled: handleRunCanceled, onJobStateChange: handleJobStateChange, onHasAnyRun: setHasAnyRun,
              onSupportedChange: setPracticeSupported }}
            logs={{ show: practiceSupported && !['github', 'multi'].includes(competition.type?.toLowerCase() || ''), busy: downloadingLogs,
              runInFlight, hasAnyRun, onDownload: handleDownloadRunLogs }} />}
          {activeTab === 'leaderboard' && <LeaderboardTab competition={competition} />}
        </div>
      </div>
    </>
  );
};

export default CompetitionPage;
